import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, MailWarning } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

export default function MagicLogin() {
  const { verifyMagicLink } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("verifying");
  const [msg, setMsg] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setMsg("This sign-in link is missing its token.");
      return;
    }
    (async () => {
      try {
        const user = await verifyMagicLink(token);
        navigate(user.role === "founder" ? "/app" : "/portal", { replace: true });
      } catch (err) {
        setStatus("error");
        setMsg(err?.response?.data?.detail || "This sign-in link is invalid or has expired.");
      }
    })();
  }, [params, verifyMagicLink, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12" data-testid="magic-login-page">
      <div className="w-full max-w-sm text-center">
        <div className="flex justify-center"><Logo /></div>
        {status === "verifying" ? (
          <div className="mt-10" data-testid="magic-verifying">
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-primary" />
            <h1 className="h-display mt-5 text-xl font-semibold text-foreground">Signing you in…</h1>
            <p className="mt-1 text-sm text-muted-foreground">Verifying your one-time sign-in link.</p>
          </div>
        ) : (
          <div className="mt-10" data-testid="magic-error">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
              <MailWarning className="h-6 w-6" />
            </div>
            <h1 className="h-display mt-5 text-xl font-semibold text-foreground">Link didn't work</h1>
            <p className="mt-1 text-sm text-muted-foreground">{msg}</p>
            <Link
              to="/login"
              className="mt-6 inline-block rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
              data-testid="magic-error-back"
            >
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
